import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { UsageDataPoint } from '../../../services/SubscriptionService';

interface ApiUsageChartProps {
  data: UsageDataPoint[];
  total: number;
  maxRequests: number | null;
  isLoading: boolean;
  range: '1d' | '7d' | '30d' | '90d';
  startDate: Date;
  endDate: Date;
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (dateStr.includes('T')) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
};

const toLocalDateKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toLocalHourKey = (d: Date): string => {
  const date = toLocalDateKey(d);
  const h = String(d.getHours()).padStart(2, '0');
  return `${date}T${h}:00:00`;
};

const generateZeroFilledData = (
  startDate: Date,
  endDate: Date,
  range: '1d' | '7d' | '30d' | '90d',
  realData: UsageDataPoint[],
): UsageDataPoint[] => {
  const buckets: UsageDataPoint[] = [];
  const isHourly = range === '1d';

  const realDataMap = new Map<string, number>();
  for (const point of realData) {
    realDataMap.set(point.date, point.count);
  }

  if (isHourly) {
    const totalHours = Math.ceil((endDate.getTime() - startDate.getTime()) / 3600000);
    for (let h = 0; h < totalHours; h++) {
      const d = new Date(startDate);
      d.setHours(d.getHours() + h, 0, 0, 0);
      const key = toLocalHourKey(d);
      buckets.push({ date: key, count: realDataMap.get(key) ?? 0 });
    }
  } else {
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000);
    for (let i = 0; i <= totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const key = toLocalDateKey(d);
      buckets.push({ date: key, count: realDataMap.get(key) ?? 0 });
    }
  }

  return buckets;
};

const MAX_VISIBLE_TICKS = 30;

const getTickInterval = (dataLength: number): number => {
  if (dataLength <= MAX_VISIBLE_TICKS) return 0;
  return Math.ceil(dataLength / MAX_VISIBLE_TICKS) - 1;
};

const ApiUsageChart: React.FC<ApiUsageChartProps> = ({ data, total, maxRequests, isLoading, range, startDate, endDate }) => {
  const filledData = useMemo(
    () => generateZeroFilledData(startDate, endDate, range, data),
    [startDate, endDate, range, data],
  );

  if (isLoading) {
    return (
      <div className="flex-[3] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <span className="text-neutral text-lg font-medium">Utilisation de l'API</span>
        </div>
        <div className="h-[200px] flex items-center justify-center">
          <span className="text-neutral-40 typo-small">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-[4] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-neutral typo-h3">Utilisation de l'API</h3>
        <span className="typo-small">
          <span className="text-neutral">{total}</span>
          <span className="text-neutral-60"> requêtes{maxRequests ? ` / ${maxRequests}` : ' / ∞'}</span>
        </span>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={filledData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid stroke="rgba(26,26,26,0.05)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
              tickLine={false}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              height={50}
              interval={getTickInterval(filledData.length)}
            />
            <YAxis
              tick={{ fontSize: 13, fill: 'rgba(26,26,26,0.6)' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickCount={5}
              width={50}
              interval={0}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1A1A1A',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '13px',
                color: 'white',
              }}
              labelFormatter={(label) => formatDate(String(label))}
              formatter={(value: any) => [`${value} requêtes`]}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#FF8A4C"
              strokeWidth={2}
              dot={{ fill: '#FF8A4C', r: 3, strokeWidth: 0 }}
              activeDot={{ fill: '#FF8A4C', r: 5, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ApiUsageChart;
